import {
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Modal,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import type { ChecklistItem } from "./api";
import { useAddChecklistItem } from "./useAddChecklistItem";
import { useChecklist } from "./useChecklist";
import { useRemoveChecklistItem } from "./useRemoveChecklistItem";

// Секция «Чек-лист готовности reviewer'а» на `/settings/reviewer-checklist`.
// Человек вводит `owner/repo`, видит список доп-пунктов проверки
// per-(org, repo), добавляет и удаляет их (source=manual). Зеркало
// `TrustRulesSection` (таблица + add + Modal-confirm на delete).

// Цвет Badge источника: правка человека (manual) vs самообучение.
function sourceColor(source: ChecklistItem["source"]): string {
  return source === "manual" ? "blue" : "grape";
}

export function ReviewerChecklistSection(): ReactElement {
  const { t } = useTranslation();
  const [repoFullName, setRepoFullName] = useState("");
  const [newText, setNewText] = useState("");
  const [deleting, setDeleting] = useState<ChecklistItem | null>(null);

  const checklist = useChecklist(repoFullName);
  const addMutation = useAddChecklistItem();
  const removeMutation = useRemoveChecklistItem();

  const trimmedRepo = repoFullName.trim();
  const repoEntered = trimmedRepo.length > 0;
  const canAdd = repoEntered && newText.trim().length > 0;

  const cancelDelete = (): void => {
    setDeleting(null);
  };

  const handleAdd = (): void => {
    addMutation.mutate(
      { repo_full_name: trimmedRepo, text: newText.trim() },
      {
        onSuccess: () => {
          setNewText("");
        },
        onError: (error) => {
          notifications.show({
            message: t("reviewerChecklist.addError", {
              message: error.message,
            }),
            color: "red",
          });
        },
      },
    );
  };

  return (
    <Stack gap="md">
      <Title order={2}>{t("reviewerChecklist.title")}</Title>
      <Text c="dimmed">{t("reviewerChecklist.intro")}</Text>

      <TextInput
        label={t("reviewerChecklist.repoLabel")}
        description={t("reviewerChecklist.repoHint")}
        placeholder={t("reviewerChecklist.repoPlaceholder")}
        value={repoFullName}
        onChange={(event) => {
          setRepoFullName(event.currentTarget.value);
        }}
      />

      {repoEntered ? (
        <ChecklistBody
          repoEntered={repoEntered}
          checklist={checklist}
          newText={newText}
          setNewText={setNewText}
          canAdd={canAdd}
          adding={addMutation.isPending}
          onAdd={handleAdd}
          onDelete={setDeleting}
          t={t}
        />
      ) : (
        <Text c="dimmed">{t("reviewerChecklist.enterRepo")}</Text>
      )}

      <Modal
        opened={deleting !== null}
        onClose={cancelDelete}
        title={t("reviewerChecklist.delete.confirmTitle")}
        centered
      >
        {deleting !== null ? (
          <Stack gap="md">
            <Text>
              {t("reviewerChecklist.delete.confirmText", {
                text: deleting.text,
              })}
            </Text>
            <Group justify="flex-end">
              <Button variant="default" onClick={cancelDelete}>
                {t("reviewerChecklist.modalCancel")}
              </Button>
              <Button
                color="red"
                onClick={() => {
                  removeMutation.mutate(
                    {
                      repoFullName: trimmedRepo,
                      itemId: deleting.id,
                    },
                    {
                      onSuccess: () => {
                        setDeleting(null);
                      },
                      onError: (error) => {
                        notifications.show({
                          message: t("reviewerChecklist.removeError", {
                            message: error.message,
                          }),
                          color: "red",
                        });
                      },
                    },
                  );
                }}
                loading={removeMutation.isPending}
              >
                {t("reviewerChecklist.delete.confirmButton")}
              </Button>
            </Group>
          </Stack>
        ) : null}
      </Modal>
    </Stack>
  );
}

// Тело секции после ввода репо: форма добавления + список пунктов.
// Вынесено отдельным компонентом ради читаемости (как Form/History у
// trustRules). Loader/Error/empty/таблица — четыре взаимоисключающих
// состояния списка.
function ChecklistBody({
  repoEntered,
  checklist,
  newText,
  setNewText,
  canAdd,
  adding,
  onAdd,
  onDelete,
  t,
}: {
  readonly repoEntered: boolean;
  readonly checklist: ReturnType<typeof useChecklist>;
  readonly newText: string;
  readonly setNewText: (value: string) => void;
  readonly canAdd: boolean;
  readonly adding: boolean;
  readonly onAdd: () => void;
  readonly onDelete: (item: ChecklistItem) => void;
  readonly t: ReturnType<typeof useTranslation>["t"];
}): ReactElement {
  return (
    <Stack gap="md">
      <Group align="flex-end" gap="sm">
        <TextInput
          style={{ flex: 1 }}
          label={t("reviewerChecklist.newItemLabel")}
          placeholder={t("reviewerChecklist.newItemPlaceholder")}
          value={newText}
          onChange={(event) => {
            setNewText(event.currentTarget.value);
          }}
          disabled={!repoEntered}
        />
        <Button onClick={onAdd} disabled={!canAdd} loading={adding}>
          {t("reviewerChecklist.addButton")}
        </Button>
      </Group>

      <ChecklistList checklist={checklist} onDelete={onDelete} t={t} />
    </Stack>
  );
}

// Список пунктов: один из четырёх взаимоисключающих результатов запроса.
function ChecklistList({
  checklist,
  onDelete,
  t,
}: {
  readonly checklist: ReturnType<typeof useChecklist>;
  readonly onDelete: (item: ChecklistItem) => void;
  readonly t: ReturnType<typeof useTranslation>["t"];
}): ReactElement {
  if (checklist.isPending) {
    return <Loader />;
  }
  if (checklist.isError) {
    return (
      <Alert color="red" title={t("reviewerChecklist.errorHeading")}>
        {t("reviewerChecklist.loadingError")}
      </Alert>
    );
  }
  if (checklist.data.length === 0) {
    return <Text c="dimmed">{t("reviewerChecklist.empty")}</Text>;
  }
  return (
    <Table withTableBorder withColumnBorders striped>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>{t("reviewerChecklist.colText")}</Table.Th>
          <Table.Th>{t("reviewerChecklist.colSource")}</Table.Th>
          <Table.Th>{t("reviewerChecklist.colManage")}</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {checklist.data.map((item) => (
          <Table.Tr key={item.id}>
            <Table.Td>{item.text}</Table.Td>
            <Table.Td>
              <Badge variant="light" color={sourceColor(item.source)}>
                {t(`reviewerChecklist.source.${item.source}`)}
              </Badge>
            </Table.Td>
            <Table.Td>
              <Button
                size="xs"
                color="red"
                variant="subtle"
                onClick={() => {
                  onDelete(item);
                }}
              >
                {t("reviewerChecklist.deleteButton")}
              </Button>
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}
